import { KeyboardEvent, useEffect, useState } from 'react';

export type OtsTimePickerProps = {
  /** Local time in 24-hour HH:mm format. */
  value?: string;
  onChange: (value: string) => void;
  minuteStep?: number;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
};

const pad = (value: number) => String(value).padStart(2, '0');
const parseTime = (value?: string) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value ?? '');
  const hour = Math.min(23, Math.max(0, Number(match?.[1] ?? 0)));
  const minute = Math.min(59, Math.max(0, Number(match?.[2] ?? 0)));
  return { hour, minute };
};

/** Compact, theme-aware segmented time input modeled after WaterGo's date/time field. */
export const OtsTimePicker = ({
  value,
  onChange,
  minuteStep = 1,
  disabled = false,
  ariaLabel = 'Time',
  className,
}: OtsTimePickerProps) => {
  const { hour, minute } = parseTime(value);
  const safeStep = Math.min(60, Math.max(1, Math.floor(minuteStep)));
  const [hourText, setHourText] = useState(pad(hour));
  const [minuteText, setMinuteText] = useState(pad(minute));
  useEffect(() => setHourText(pad(hour)), [hour]);
  useEffect(() => setMinuteText(pad(minute)), [minute]);

  const normalizedPart = (text: string, maximum: number, fallback: number) => {
    if (!/^\d{1,2}$/.test(text)) return fallback;
    return Math.min(maximum, Math.max(0, Number(text)));
  };
  const commit = (nextHourText = hourText, nextMinuteText = minuteText) => {
    const nextHour = normalizedPart(nextHourText, 23, hour);
    const nextMinute = normalizedPart(nextMinuteText, 59, minute);
    setHourText(pad(nextHour));
    setMinuteText(pad(nextMinute));
    onChange(`${pad(nextHour)}:${pad(nextMinute)}`);
  };
  const stepPart = (part: 'hour' | 'minute', direction: 1 | -1) => {
    if (part === 'hour') {
      const nextHour =
        (normalizedPart(hourText, 23, hour) + direction + 24) % 24;
      commit(String(nextHour), minuteText);
    } else {
      const nextMinute =
        (normalizedPart(minuteText, 59, minute) + direction * safeStep + 60) %
        60;
      commit(hourText, String(nextMinute));
    }
  };
  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    part: 'hour' | 'minute'
  ) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      stepPart(part, event.key === 'ArrowUp' ? 1 : -1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit();
      event.currentTarget.select();
    }
  };
  const inputClassName =
    'h-7 w-8 rounded bg-transparent px-1 text-center text-xs font-semibold tabular-nums text-[var(--ots-text)] outline-none transition-colors hover:bg-[var(--ots-surface-variant)] focus:bg-[var(--ots-primary-container)] focus:text-[var(--ots-primary)] disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={[
        'inline-flex items-center rounded-lg border border-[var(--ots-field-outline)] bg-[var(--ots-field)] p-0.5 shadow-xs focus-within:border-[var(--ots-primary)] focus-within:ring-3 focus-within:ring-[var(--ots-primary)]/15',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        aria-label={`${ariaLabel} hour`}
        disabled={disabled}
        value={hourText}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => {
          if (/^\d{0,2}$/.test(event.target.value))
            setHourText(event.target.value);
        }}
        onBlur={() => commit()}
        onKeyDown={(event) => handleKeyDown(event, 'hour')}
        className={inputClassName}
      />
      <span
        aria-hidden="true"
        className="text-sm font-bold text-[var(--ots-text-muted)]"
      >
        :
      </span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        aria-label={`${ariaLabel} minute`}
        disabled={disabled}
        value={minuteText}
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => {
          if (/^\d{0,2}$/.test(event.target.value))
            setMinuteText(event.target.value);
        }}
        onBlur={() => commit()}
        onKeyDown={(event) => handleKeyDown(event, 'minute')}
        className={inputClassName}
      />
      <span className="pr-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
        24h
      </span>
    </div>
  );
};
